import { TeamRagThresholds } from "@coaching-culture/types";
import { useUpdateTeamRagThresholds } from "@coaching-culture/api-client";
import {
  Box,
  Button,
  Flex,
  FormInput,
  IconToast,
  Label,
  Panel,
  PanelHeader,
  Rule,
  Text,
  useToast,
} from "@coaching-culture/ui";
import axios from "axios";
import CenterColumn from "components/CenterColumn";
import { PageHeader } from "components/PageHeader";
import {
  defaultTeamRagThresholds,
  metricStatusLabels,
} from "pages/TeamView/metrics";
import { useState } from "react";
import {
  FaBullseye,
  FaChartBar,
  FaCommentDots,
  FaEnvelope,
  FaSave,
  FaUndo,
} from "react-icons/fa";
import { IconType } from "react-icons";
import { useGoalTerminology, useOrg } from "auth/OrgProvider";

type SectionProps = {
  icon: IconType;
  title: string;
  description: string;
  children: React.ReactNode;
};

const Section = ({
  icon: Icon,
  title,
  description,
  children,
}: SectionProps) => (
  <Box mb={4}>
    <Flex alignItems="center" mb={1}>
      <Text color="primary" mr={2} lineHeight={0}>
        <Icon />
      </Text>
      <Text fontWeight={600} fontSize={4}>
        {title}
      </Text>
    </Flex>
    <Text color="textMuted" fontSize="small" mb={2}>
      {description}
    </Text>
    <Flex flexDirection={["column", "row"]} style={{ gap: 12 }}>
      {children}
    </Flex>
  </Box>
);

export const TeamStatusThresholds = () => {
  const [org] = useOrg();
  const terms = useGoalTerminology();
  const toast = useToast();
  const update = useUpdateTeamRagThresholds();

  const [values, setValues] = useState<TeamRagThresholds>(
    org?.teamRagThresholds ?? defaultTeamRagThresholds,
  );
  const [error, setError] = useState<string | null>(null);

  const setField = (field: keyof TeamRagThresholds) => (ev: any) => {
    const val = parseInt(ev.target.value, 10);
    setValues((old) => ({ ...old, [field]: isNaN(val) ? 0 : val }));
  };

  const save = async () => {
    setError(null);

    try {
      await update.mutateAsync({ orgId: org.id, ...values });
      toast({
        content: (
          <IconToast icon={FaSave} iconColor="positive" text="Settings Saved" />
        ),
      });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setError(err.response.data?.message?.error ?? "Invalid thresholds");
      } else if (axios.isAxiosError(err) && err.response?.status === 400) {
        setError("All thresholds must be whole numbers of at least 1 day");
      } else {
        setError("Unable to save settings right now.");
      }
    }
  };

  const reset = () => {
    setError(null);
    setValues(defaultTeamRagThresholds);
  };

  return (
    <CenterColumn>
      <PageHeader
        text="Team Status Thresholds"
        subtitle="Control when the My Team page flags a direct report's goals, conversations, feedback and measures"
      />
      <Panel>
        <PanelHeader p={3}>
          <Text fontWeight={600} fontSize={4} as="h2">
            Status Rules
          </Text>
        </PanelHeader>
        <Box p={3}>
          <Text color="textMuted" mb={4}>
            Each section of a team member's card shows{" "}
            <Label size="small" color="positive">
              {metricStatusLabels.positive}
            </Label>
            ,{" "}
            <Label size="small" color="warning">
              {metricStatusLabels.warning}
            </Label>{" "}
            or{" "}
            <Label size="small" color="danger">
              {metricStatusLabels.danger}
            </Label>{" "}
            based on the number of days below. All values are in days.
          </Text>
          <Section
            icon={FaBullseye}
            title={terms.goal.asPluralTitle()}
            description={`How long someone can be without an active ${terms.goal.asBody()} before their ${terms.goal.asPluralBody()} need attention.`}
          >
            <FormInput
              label={`Days without an active ${terms.goal.asBody()}`}
              type="number"
              value={values.goalsStaleDays.toString()}
              onChange={setField("goalsStaleDays")}
            />
          </Section>
          <Section
            icon={FaCommentDots}
            title="Conversations"
            description="On track needs a conversation completed within this window and another scheduled inside it. Needs attention means neither."
          >
            <FormInput
              label="Conversation window"
              type="number"
              value={values.convoDays.toString()}
              onChange={setField("convoDays")}
            />
          </Section>
          <Section
            icon={FaEnvelope}
            title="Continuous Feedback"
            description="Based on the days since the team member last received any feedback."
          >
            <FormInput
              label="On track within"
              type="number"
              value={values.feedbackGreenDays.toString()}
              onChange={setField("feedbackGreenDays")}
            />
            <FormInput
              label="At risk until"
              type="number"
              value={values.feedbackAmberDays.toString()}
              onChange={setField("feedbackAmberDays")}
            />
          </Section>
          <Section
            icon={FaChartBar}
            title="Measures"
            description="Based on the days since the team member's measures were last reviewed."
          >
            <FormInput
              label="On track within"
              type="number"
              value={values.measuresGreenDays.toString()}
              onChange={setField("measuresGreenDays")}
            />
            <FormInput
              label="At risk until"
              type="number"
              value={values.measuresAmberDays.toString()}
              onChange={setField("measuresAmberDays")}
            />
          </Section>
          <Rule />
          {error && (
            <Text color="danger" fontWeight={500} mb={2}>
              {error}
            </Text>
          )}
          <Flex style={{ gap: 8 }}>
            <Button
              variant="primary"
              icon={FaSave}
              onClick={save}
              disabled={update.isLoading}
            >
              Save
            </Button>
            <Button icon={FaUndo} onClick={reset}>
              Reset to Defaults
            </Button>
          </Flex>
        </Box>
      </Panel>
    </CenterColumn>
  );
};

export default TeamStatusThresholds;
